import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Card, CardHeader } from 'reactstrap';

import useScrollToElement from '../../../../hooks/dom/useScrollToElement';
import useAlerts from '../../../../hooks/order/useAlerts';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import UIStore from '../../../../store/UIStore';
import ModuleHeader from '../ModuleHeader';

const CheckoutModule = ({
  checkoutStore,
  uiStore,
  errorAlerts,
  generalAlerts,
  className,
  title,
  children,
  orderNumber,
  module,
  headerLogo,
  headerActions,
  alertsBelow,
}) => {
  const { order, scrollToAlertCounter } = checkoutStore;
  const { scroll_to_alert } = order;

  const [ref] = useScrollToElement({
    elementIdToScroll: scroll_to_alert,
    scrollTrigger: scrollToAlertCounter,
  });
  const { renderErrorAlerts, renderGeneralAlerts } = useAlerts({
    generalAlerts,
    errorAlerts,
  });

  const getId = () => {
    if (module) {
      return module.toLowerCase();
    }

    return '';
  };

  const renderGeneralAlertsBlock = () =>
    generalAlerts?.length > 0 ? (
      <div className="CheckoutModule__general-alerts">
        {renderGeneralAlerts()}
      </div>
    ) : null;
  const renderErrorAlertsBlock = () =>
    errorAlerts?.length > 0 ? renderErrorAlerts() : null;

  const scrollMarginTop = Math.max(50, uiStore?.headerHeight ?? 50);

  return (
    <section
      ref={ref}
      id={getId()}
      className={classNames('CheckoutModule', className)}
      style={{ scrollMarginTop }}
    >
      {!alertsBelow && renderGeneralAlertsBlock()}
      <Card
        className={classNames({
          'CheckoutModule--error': errorAlerts?.length && !alertsBelow,
        })}
      >
        <CardHeader className="CheckoutModule__header">
          {headerLogo && (
            <div className="CheckoutModule__header-logo">{headerLogo}</div>
          )}
          <ModuleHeader>
            {orderNumber && `${orderNumber}. `}
            {title}
          </ModuleHeader>
          {headerActions && (
            <div className="CheckoutModule__header-actions">
              {headerActions}
            </div>
          )}
          {!alertsBelow && renderErrorAlertsBlock()}
        </CardHeader>
        {children}
      </Card>
      {alertsBelow && (
        <>
          {renderGeneralAlertsBlock()}
          {errorAlerts?.length > 0 && (
            <div className="CheckoutModule__error-alerts">
              {renderErrorAlerts()}
            </div>
          )}
        </>
      )}
    </section>
  );
};

CheckoutModule.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  uiStore: modelOf(UIStore),
  orderNumber: PropTypes.number,
  className: PropTypes.string,
  module: PropTypes.string,
  title: PropTypes.node,
  headerLogo: PropTypes.node,
  headerActions: PropTypes.node,
  alertsBelow: PropTypes.bool,
};

export default inject('checkoutStore', 'uiStore')(observer(CheckoutModule));
